import { useQuery } from '@tanstack/react-query'

export interface DoctrineModeInfo {
  mode: 'lite' | 'full'
  disabledRoutes: string[]
  disabledFeatures: string[]
  version: string
}

const DEFAULT_MODE_INFO: DoctrineModeInfo = {
  mode: 'full',
  disabledRoutes: [],
  disabledFeatures: [],
  version: 'unknown',
}

async function fetchDoctrineMode(): Promise<DoctrineModeInfo> {
  try {
    const res = await fetch('/api/health/mode', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: AbortSignal.timeout(5000),
    })
    if (!res.ok) return DEFAULT_MODE_INFO
    const data = (await res.json()) as Partial<DoctrineModeInfo>
    return {
      mode: data.mode === 'lite' ? 'lite' : 'full',
      disabledRoutes: Array.isArray(data.disabledRoutes) ? data.disabledRoutes : [],
      disabledFeatures: Array.isArray(data.disabledFeatures) ? data.disabledFeatures : [],
      version: typeof data.version === 'string' ? data.version : 'unknown',
    }
  } catch {
    // On fetch failure, assume full mode. Never block the app.
    return DEFAULT_MODE_INFO
  }
}

export function useDoctrineMode() {
  const { data } = useQuery({
    queryKey: ['doctrine-mode'],
    queryFn: fetchDoctrineMode,
    staleTime: Infinity,
    retry: false,
  })

  const info = data ?? DEFAULT_MODE_INFO

  return {
    mode: info.mode,
    isLite: info.mode === 'lite',
    isFull: info.mode === 'full',
    disabledRoutes: info.disabledRoutes,
    disabledFeatures: info.disabledFeatures,
    version: info.version,
    isFeatureAvailable: (feature: string) => !info.disabledFeatures.includes(feature),
    isRouteAvailable: (routeName: string) => !info.disabledRoutes.includes(routeName),
  }
}
